import { action, computed, observable } from 'mobx';

import { analytics } from 'core/analytics';
import { routerStore } from 'core/routing';
import { AppManager, Input } from 'core/index';

import {
  ConvertAssemblyMutation,
  CADConversionSubscription,
  ExtractPartMutation,
} from 'apps/conversions/gql';

import Alert from 'react-s-alert';

export const CAD_TASK_STATUS = {
  UNKNOWN: '',
  CONVERTING: 'converting',
  EXTRACTING: 'extracting',
  FINISHED: 'finished',
  FAILED: 'failed',
  PENDING: 'pending',
  STARTED: 'started',
  RETRY: 'retry',
};

export const forceFileDownloadFromURL = fileURL => {
  const link = document.createElement('a');
  link.href = fileURL;
  link.setAttribute(
    'download',
    fileURL
      .split(/(\\|\/)/g)
      .pop()
      .split('?')[0],
  );
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

class CadConvertError extends Error {
  constructor(message) {
    super(message);
    this.name = 'CadConvertError';
  }
}

export class ConversionManager extends AppManager {
  @observable fileId = null;
  @observable format = null;
  @observable status = null;

  constructor(parent, init = {}) {
    super(parent, init);

    this.fileId = init.fileId;
    this.format = init.format || '';
    this.retry = init.retry || false;
    this.status = CAD_TASK_STATUS.UNKNOWN;
    this.cadConversionInput = new Input({
      fileId: () => this.fileId,
      format: () => this.format,
    });
    this.disposers.push(() => {
      // - when the appManger disposer is called (when DownloadButton is unmounted),
      // then the subscription is cancelled, so we need to set this.status to UNKNOWN,
      // otherwise the statusText will return Converting... making the DownloadButton
      // stuck in that state
      this.status = CAD_TASK_STATUS.UNKNOWN;
    });
  }

  @action StartCadConversion = options => {
    this.mutations.push(
      ConvertAssemblyMutation.New(this.cadConversionInput, options),
    );
  };

  findSpecificConversionInArray = (cadConvertArray, format) => {
    const conversionForFormat = cadConvertArray.find(obj => {
      return obj.format === format && obj.cadId === null;
    });
    return conversionForFormat;
  };

  @action StartProcess = () => {
    this.SubscribeCADConversionStatus();
    this.StartCadConversion({
      onSysErr: this.onMutateCadConversionError,
      onUserErr: this.onMutateCadConversionError,
      onSuccess: this.onMutateCadConversionSuccess,
    });
  };

  @action onMutateCadConversionError = err => {
    analytics.logError(new CadConvertError(err));
  };

  @action onMutateCadConversionSuccess = conversions => {
    const conversionForThisFormat = this.findSpecificConversionInArray(
      conversions,
      this.format,
    );

    if (!conversionForThisFormat) {
      // - log error about a successful conversion for a format that
      // should not have been converted
      this.onMutateCadConversionError(
        'successful CAD conversion without valid format',
      );
    } else {
      // - if the CAD conversion is already finished, the subscription
      // will never send a finished message, call handleCadConversionStatusMessage
      // manually to make sure an immediate failed/finished conversion
      // is handled correctly
      this.handleCadConversionStatusMessage(conversionForThisFormat);
    }
  };

  @action SubscribeCADConversionStatus = () => {
    this.status = CAD_TASK_STATUS.CONVERTING;
    this.cadStatusSubscription = new CADConversionSubscription(this, {
      variables: {
        subscribeCADConversionInput: {
          fileId: this.fileId,
          format: this.format,
        },
      },
    });
    this.cadStatusSubscription.process(this.handleCadConversionStatusMessage);
    this.cadStatusSubscription.StartSubscription();
  };

  @action handleCadConversionStatusMessage = message => {
    if (message.status === CAD_TASK_STATUS.PENDING) {
      Alert.success(
        'CAD conversion started, this can take a moment. If it takes too long you can also come back later and download it.',
      );
    } else if (message.status === CAD_TASK_STATUS.FINISHED) {
      this.cadStatusSubscription?.CompleteSubscription();

      if (!message.url) {
        Alert.error(
          'Sorry, there was a problem downloading the file. Please try again later.',
        );
        analytics.logEvent(
          'missing message.url after successful CAD conversion',
        );
        this.status = CAD_TASK_STATUS.FAILED;
      } else {
        Alert.success('CAD conversion finished.');
        this.status = CAD_TASK_STATUS.FINISHED;
        forceFileDownloadFromURL(message.url);
      }
    } else if (message.status === CAD_TASK_STATUS.FAILED) {
      this.cadStatusSubscription?.CompleteSubscription();

      this.status = CAD_TASK_STATUS.FAILED;
      Alert.error(`Sorry, there was an error when converting your file.`);
      this.onMutateCadConversionError(
        `failed CAD conversion: ${message.error}`,
      );
    }
  };

  get statusText() {
    switch (this.status) {
      case CAD_TASK_STATUS.CONVERTING:
        return 'Converting file...';

      case CAD_TASK_STATUS.FINISHED:
        return `Download as .${this.format.toLowerCase()}`;

      case CAD_TASK_STATUS.FAILED:
        return `Error converting to .${this.format.toLowerCase()}`;

      default:
        return '';
    }
  }
}

class PartExtractionError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PartExtractionError';
  }
}

export class PartExtractionManager extends AppManager {
  @observable fileId = null;
  @observable partId = null;

  @observable rfqInput = null;

  constructor(parent, init = {}) {
    super(parent, init);

    this.fileId = init.fileId;
    this.partId = init.partId;
    this.retry = init.retry || false;
    this.creatorId = init.creatorId || null;
    this.rfqInput = init.rfqInput || null;
    this.options = init.options || null;
    this.partFilename = init.partFilename || null;

    this.partExtractionInput = new Input({
      fileId: () => this.fileId,
      cadIds: () => [this.partId],
      partFilenames: () => (init.partFilename ? [this.partFilename] : null),
    });
    this.partExtractionInputWithRFQ = new Input({
      fileId: () => this.fileId,
      cadIds: () => [this.partId],
      requestForQuote: () => this.rfqInput,
      partFilenames: () => (init.partFilename ? [this.partFilename] : null),
    });
  }

  StartProcess = () => {
    this.StartPartExtraction({
      onSysErr: this.onMutatePartExtractionError,
      onUserErr: this.onMutatePartExtractionError,
      onSuccess: this.onMutatePartExtractionSuccess,
    });
  };

  onMutatePartExtractionError = err => {
    analytics.logError(new PartExtractionError(err));
  };

  onMutatePartExtractionSuccess = rfqs => {
    const rfqForCadId = this.findRfqInArray(rfqs, this.partId);

    if (!rfqForCadId) {
      Alert.error(
        'Sorry there was a problem creating a RFQ for the extracted part, please try again.',
      );
      this.onMutatePartExtractionError(
        'successful extraction without valid RFQ',
      );
    } else {
      this.redirectToRfq(rfqForCadId);

      if (!rfqForCadId.jobSpec?.build?.body) {
        return;
      }

      const bodyStatus = rfqForCadId.jobSpec.build.body.status;
      const bodyError = rfqForCadId.jobSpec.build.body.error;
      if (bodyStatus === CAD_TASK_STATUS.FINISHED && !bodyError) {
        Alert.success('Part extraction finished');
      } else if (
        bodyStatus === CAD_TASK_STATUS.PENDING ||
        bodyStatus === CAD_TASK_STATUS.STARTED ||
        bodyStatus === CAD_TASK_STATUS.RETRY
      ) {
        Alert.success(
          'We are extracting the part and creating the RFQ for you. Please, hold on for a second',
        );
      } else if (bodyStatus === CAD_TASK_STATUS.FAILED) {
        Alert.error(
          'Sorry, there was a problem while preparing the extracted part CAD model.',
        );
        this.onMutatePartExtractionError(
          `body error after extracting part: ${bodyError}`,
        );
      }
    }
  };

  redirectToRfq = rfq => {
    const rfqId = rfq.id;
    if (rfqId !== undefined) {
      routerStore.router.navigate('rfq', {
        rfqId,
        partId: this.partId,
      });
    } else {
      Alert.error(
        'Sorry there was a problem creating a RFQ for the extracted part, please try again.',
      );
      this.onMutatePartExtractionError(
        'failed to redirect to RFQ after successful extraction',
      );
    }
  };

  StartPartExtraction = options => {
    if (this.rfqInput === null) {
      this.mutations.push(
        ExtractPartMutation.New(this.partExtractionInput, options),
      );
    } else {
      this.mutations.push(
        ExtractPartMutation.New(this.partExtractionInputWithRFQ, options),
      );
    }
  };

  findRfqInArray = (rfqsArray, cadId) => {
    const rfqForCadId = rfqsArray.find(rfq => {
      return rfq.jobSpec?.build?.body?.cadId === cadId;
    });
    return rfqForCadId;
  };
}

export class ConversionsStore {
  static registryName = 'ConversionsAppManager';

  @observable conversionOperations = null;
  @observable extractionOperations = null;

  constructor() {
    this.conversionOperations = {};
    this.extractionOperations = {};
  }

  @action StartNewConversionOperation = (fileId, format) => {
    let manager;
    const managerIdentifier = `${fileId}-${format}`;
    if (this.conversionOperations[managerIdentifier] === undefined) {
      manager = new ConversionManager(this, {
        fileId,
        format,
      });
      this.conversionOperations[managerIdentifier] = manager;
    } else {
      manager = this.conversionOperations[managerIdentifier];
    }

    manager.StartProcess();
  };

  @action StartExtraction = (
    fileId,
    partFilename,
    partId,
    creatorId,
    rfqInput,
    options = {},
  ) => {
    const partIdentifier = `${partId}`;
    let manager;
    if (this.extractionOperations[partIdentifier] === undefined) {
      manager = new PartExtractionManager(this, {
        fileId,
        partFilename,
        partId,
        creatorId,
        rfqInput,
        options,
      });
      this.extractionOperations[partIdentifier] = manager;
    } else {
      manager = this.extractionOperations[partIdentifier];
    }

    manager.StartProcess();
  };
}

export const conversionsStore = new ConversionsStore();
